import type {Language} from '../i18n';

/**
 * Booking date and time, composed and displayed **as wall time** — never
 * projected through a device timezone.
 *
 * The booking flow has no venue timezone. It is reached from a booking link, not
 * a scanned QR, so no service-status read has run and `I18nProvider`'s `time()`
 * would return the empty string (`ZONE_UNKNOWN`). More importantly, a booking is
 * a wall-clock intent: "19:00 at the venue" must read 19:00 to a guest whose
 * phone is on Central European time, not 14:00. So a booking time is carried as a
 * naive local string `YYYY-MM-DDTHH:mm:00`, and every label here reads the wall
 * components straight off that string. The server resolves it into the venue's
 * real zone; the client never guesses one.
 *
 * The one place a real instant is needed — "is it within 2 hours of now" in
 * BookManage — is a duration, and `Date.parse` of the same string is close
 * enough for a *hint*; the server enforces the true window and answers 409.
 */

const DATE_LOCALE: Record<Language, string> = {
  th: 'th-TH',
  en: 'en-GB',
  zh: 'zh-Hans',
};

export interface DateOption {
  /** `YYYY-MM-DD`. */
  iso: string;
  /** Day of month, for the tall date button. */
  day: number;
  isToday: boolean;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * A UTC `Date` at noon for an ISO calendar date, used only to feed `Intl` for a
 * label. Noon-UTC so the day cannot slip either way whatever `Intl` is given.
 */
function dateFromIso(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
}

/** The next `count` calendar days from the device's clock, today first. */
export function upcomingDates(count: number, now: Date = new Date()): DateOption[] {
  const todayIso = isoDate(now);
  const out: DateOption[] = [];
  for (let i = 0; i < count; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
    const iso = isoDate(d);
    out.push({iso, day: d.getDate(), isToday: iso === todayIso});
  }
  return out;
}

/** Short weekday for a date button — "จ.", "Mon", "周一". */
export function weekdayShort(iso: string, language: Language): string {
  const d = dateFromIso(iso);
  if (!d) return '';
  return new Intl.DateTimeFormat(DATE_LOCALE[language], {weekday: 'short', timeZone: 'UTC'}).format(d);
}

/** A readable date — "21 ก.ย.", "21 Sept", "9月21日". */
export function dateLabel(iso: string, language: Language): string {
  const d = dateFromIso(iso);
  if (!d) return iso;
  return new Intl.DateTimeFormat(DATE_LOCALE[language], {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(d);
}

/**
 * The bookable time slots.
 *
 * Fixed 11:00–21:30 in 30-minute steps, because the guest booking flow has **no
 * source for the venue's real trading hours or free tables** — no guest-credential
 * endpoint in the contract exposes either. So the slots are offered and the
 * server is the authority: an out-of-hours or full slot comes back as a
 * validation error, which BookWeb surfaces, rather than being greyed out from
 * knowledge the client does not have. If a hours/availability read for guests
 * ever lands, this is where it replaces the constant.
 */
export function defaultTimeSlots(): string[] {
  const slots: string[] = [];
  for (let h = 11; h <= 21; h += 1) {
    slots.push(`${pad(h)}:00`);
    if (h !== 21) slots.push(`${pad(h)}:30`);
  }
  slots.push('21:30');
  return slots;
}

/** `YYYY-MM-DD` + `HH:mm` → the naive wall time the create call sends. */
export function composeBookingTime(dateIso: string, time: string): string {
  return `${dateIso}T${time}:00`;
}

/** Read the wall date and time back out of a stored or server booking time. */
export function splitBookingTime(bookingTime: string): {dateIso: string; time: string} | null {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(bookingTime);
  return m ? {dateIso: m[1]!, time: m[2]!} : null;
}

/** Whole minutes from now until a booking time. Null when it cannot be read. A hint only. */
export function minutesUntil(bookingTime: string, now: number = Date.now()): number | null {
  const t = Date.parse(bookingTime);
  if (Number.isNaN(t)) return null;
  return Math.floor((t - now) / 60_000);
}

/** The free-cancellation window, in minutes. Mirrors the server's 2-hour rule. */
export const CANCEL_WINDOW_MINUTES = 120;
